import { PageHeader } from '../components/PageHeader';
import { DocumentIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useQuery } from '@tanstack/react-query';
import backend from '../services/backend';
import { SortableTable } from '../components/SortableTable';
import { TableSkeleton } from '../components/Skeleton';
import type { UploadSchema } from '@avisoma/shared';
import { Link } from '../components/Link';
import { ID } from '../components/ID';
import { Button } from '../catalyst/button';
import { formatDateTime } from '../lib/dateFormat';

export default function Uploads() {
  useDocumentTitle('Uploads');
  return (
    <div>
      {/* Header */}
      <PageHeader title="Uploads" description="Manage your uploads" icon={<DocumentIcon className="size-6" />}>
        <div className="flex items-center gap-4 w-full justify-between md:justify-end">
          <Button color="indigo" href="/documents/upload">
            <PlusIcon className="size-5" />
            Upload New
          </Button>
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
        <UploadsTable />
      </div>
    </div>
  );
}

function UploadsTable() {
  const uploadsQuery = useQuery({
    queryKey: ['uploads'],
    queryFn: () =>
      backend
        .listUploads({
          limit: 100,
        })
        .then((res) => res.data),
    refetchInterval: 5_000,
  });

  if (uploadsQuery.isLoading) {
    return <TableSkeleton />;
  }

  if (uploadsQuery.error) {
    return (
      <div className="text-red-600 dark:text-red-400 text-center text-sm pt-12">
        {(uploadsQuery.error as Error).message}
      </div>
    );
  }

  if (!uploadsQuery.data?.items.length) {
    return (
      <div className="text-gray-500 dark:text-zinc-400 text-center text-xl pt-12 font-extralight">
        No uploads found.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg">
      <SortableTable
        storageId="uploads"
        limitHeight={false}
        data={uploadsQuery.data.items}
        columns={[
          {
            accessor: 'id',
            label: 'ID',
            render: (row) => (
              <Link to={`/uploads/${row.id}`} className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                <ID id={row.id} />
              </Link>
            ),
          },
          {
            accessor: 'input',
            label: 'Input',
            render: (row) => <InputSummary input={row.input} />,
          },
          {
            accessor: 'documentCount',
            label: 'Documents',
            render: (row) => row.documentCount,
          },
          {
            accessor: 'userId',
            label: 'User ID',
            render: (row) => row.username,
          },
          {
            accessor: 'createdAt',
            label: 'Created',
            render: (row) => formatDateTime(row.createdAt),
          },
        ]}
        rowKey={(row) => row.id}
      />
    </div>
  );
}

function InputSummary({ input }: { input: UploadSchema<string>['input'] }) {
  return (
    <div className="flex items-center gap-2">
      <span>URLs: {input.urls.length ?? 0}</span>
      <span>Files: {input.files?.length ?? 0}</span>
    </div>
  );
}
